import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PostComposer } from '../components/PostComposer';
import { useSocialAccounts } from '../hooks/useSocialAccounts';
import { usePostComposer } from '../hooks/usePostComposer';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import api, { AdaptedPlatformContent, SocialMediaPost } from '@/lib/api';

export function ComposerPage() {
  const { postId } = useParams<{ postId?: string }>();
  const navigate = useNavigate();
  const { accounts } = useSocialAccounts();
  const [existingPost, setExistingPost] = useState<SocialMediaPost | null>(null);
  const [isLoadingPost, setIsLoadingPost] = useState(!!postId);

  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    api
      .getSocialMediaPost(postId)
      .then((post) => {
        if (!cancelled) {
          setExistingPost(post);
          setIsLoadingPost(false);
        }
      })
      .catch(() => {
        if (!cancelled) setIsLoadingPost(false);
      });
    return () => {
      cancelled = true;
    };
  }, [postId]);

  const {
    post,
    content,
    setContent,
    targetAccountIds,
    setTargetAccountIds,
    isSaving,
    error,
    saveDraft,
    adaptContent,
    schedulePost,
    publishNow,
  } = usePostComposer(existingPost ?? undefined);

  const [isAdapting, setIsAdapting] = useState(false);

  const handleAdapt = async (platforms?: string[]) => {
    setIsAdapting(true);
    // First save if needed
    if (!post && content.trim()) {
      await saveDraft();
    }
    await adaptContent(platforms);
    setIsAdapting(false);
  };

  const handleSchedule = async (publishAt: string) => {
    if (!post) {
      const saved = await saveDraft();
      if (!saved) return;
    }
    const scheduled = await schedulePost(publishAt);
    if (scheduled) {
      navigate('/a/arche_social_media/scheduled');
    }
  };

  const handleSavePlatformEdits = async (
    platformContent: Record<string, AdaptedPlatformContent>,
  ) => {
    if (!post) return;
    await api.updateSocialMediaPost(post.postId, { platformContent });
  };

  const handlePublishNow = async () => {
    const activeAccounts = accounts.filter((a) => a.status === 'active');
    const effectiveAccountIds =
      targetAccountIds.length > 0 ? targetAccountIds : activeAccounts.map((a) => a.accountId);

    if (effectiveAccountIds.length === 0) return;

    const saved = await saveDraft(effectiveAccountIds);
    if (!saved) return;

    const results = await publishNow(saved.postId);
    if (results) {
      navigate('/a/arche_social_media/published');
    }
  };

  if (isLoadingPost) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/a/arche_social_media/')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">{postId ? 'Edit Post' : 'New Post'}</h1>
      </div>

      <PostComposer
        post={post}
        content={content}
        setContent={setContent}
        targetAccountIds={targetAccountIds}
        setTargetAccountIds={setTargetAccountIds}
        accounts={accounts}
        isSaving={isSaving}
        error={error}
        onSaveDraft={saveDraft}
        onAdapt={handleAdapt}
        onSchedule={handleSchedule}
        onPublishNow={handlePublishNow}
        onSavePlatformEdits={handleSavePlatformEdits}
        isAdapting={isAdapting}
      />
    </div>
  );
}
